import { Box } from '@material-ui/core';
import { DataGrid, GridColDef } from '@material-ui/data-grid';
import { format, parseISO } from 'date-fns';
import { nanoid } from 'nanoid';
import React, { useCallback, useEffect, useState } from 'react';
import api from '~/services/api';
import { moneyFormat } from '~/utils/functions';
import { AgendamentoSelecionadosStorage } from '../../class/AgendamentoSelecionadosStorage';
import { AgendamentosProps, GridAgendamentoProps } from '../../types';

export const GridAgendamento: React.FC<GridAgendamentoProps> = ({
  codPessoa,
  codLoja,
  modalOpen,
  codTela,
  tipoOperacao = undefined,
}) => {
  const [agendamentos, setAgendamentos] = useState<any[]>([]);
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const indexAgendamentos = useCallback(async () => {
    setLoading(true);

    const { data } = await api.get(
      `/agendamento-de-contas/agendamentos/${codPessoa}/${codLoja}`,
    );

    let arrAgendamentos = [];

    if (data.success && data.data?.length > 0) {
      arrAgendamentos = (data.data as AgendamentosProps[]).map(
        (agendamento: any) => ({
          id: nanoid(),
          tipo: agendamento.tipo === 0 ? 'Pagar' : 'Receber',
          dta_vencimento: format(
            parseISO(agendamento.dta_vencimento),
            'dd/MM/yyyy',
          ),
          val_parcela: `R$ ${moneyFormat(agendamento.val_parcela?.toFixed(2))}`,
          des_finalizadora: agendamento.des_finalizadora,
          cod_conta: agendamento.cod_conta,
          cod_parcela: agendamento.cod_parcela,
          cod_finalizadora: agendamento.cod_finalizadora,
        }),
      );

      switch (tipoOperacao) {
        case 0:
          arrAgendamentos = arrAgendamentos.filter((ag) => ag.tipo === 'Pagar');
          break;
        case 1:
          arrAgendamentos = arrAgendamentos.filter(
            (ag) => ag.tipo === 'Receber',
          );
          break;
        default:
          break;
      }

      setAgendamentos(arrAgendamentos);
      AgendamentoSelecionadosStorage.setQtd(arrAgendamentos.length, codTela);

      const selecionadosStorage: any[] =
        AgendamentoSelecionadosStorage.get(codTela) || [];

      const idsSelecionados = arrAgendamentos
        .filter((item) =>
          selecionadosStorage
            .filter((s) => s.confirmed === true)
            .some(
              (stored) =>
                stored.cod_conta === item.cod_conta &&
                stored.cod_finalizadora === item.cod_finalizadora &&
                stored.cod_parcela === item.cod_parcela,
            ),
        )
        .map((item) => item.id);

      setSelecionados(idsSelecionados);
    } else {
      setAgendamentos([]);
    }

    setLoading(false);
  }, [codLoja, codPessoa, codTela, tipoOperacao]);

  useEffect(() => {
    if (modalOpen) indexAgendamentos();
  }, [indexAgendamentos, modalOpen]);

  useEffect(() => {
    if (!modalOpen) {
      setAgendamentos([]);
      setSelecionados([]);
    }
  }, [modalOpen]);

  const handleCheckboxToggle = (id: string) => {
    const storedItems = AgendamentoSelecionadosStorage.get(codTela);

    const current = agendamentos.find((a) => a.id === id);
    if (!current) return;

    if (
      storedItems.some(
        (item) => item.cod_parcela === current.cod_parcela && item.confirmed,
      )
    ) {
      return;
    }

    const novoSelecionados = selecionados.includes(id)
      ? selecionados.filter((itemId) => itemId !== id)
      : [...selecionados, id];

    setSelecionados(novoSelecionados);

    const updatedForStorage = novoSelecionados.map((selId) => {
      const ag = agendamentos.find((a) => a.id === selId)!;
      const prev = storedItems.find(
        (item) => item.cod_parcela === ag.cod_parcela,
      );
      return {
        ...ag,
        confirmed: prev?.confirmed ?? false,
      };
    });

    AgendamentoSelecionadosStorage.set(updatedForStorage, codTela);
  };

  const columns: GridColDef[] = [
    {
      field: 'associar',
      headerName: 'Associar?',
      width: 110,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,

      renderCell: (params) => {
        const stored = AgendamentoSelecionadosStorage.get(codTela);

        const isConfirmed = stored.some(
          (s) =>
            s.cod_parcela === params.row.cod_parcela && s.confirmed === true,
        );

        return (
          <input
            type="checkbox"
            checked={selecionados.includes(params.row.id)}
            onChange={() => handleCheckboxToggle(params.row.id)}
            disabled={isConfirmed}
          />
        );
      },
    },
    { field: 'id', hide: true },
    { field: 'cod_parcela', hide: true },
    { field: 'cod_conta', hide: true },
    {
      field: 'tipo',
      headerName: 'Tipo',
      editable: false,
      headerAlign: 'center',
      align: 'center',
      width: 120,
      hideSortIcons: true,
    },
    {
      field: 'dta_vencimento',
      headerName: 'Dta. Vcto.',
      editable: false,
      headerAlign: 'center',
      align: 'center',
      width: 150,
      hideSortIcons: true,
    },
    {
      field: 'val_parcela',
      headerName: 'Valor',
      editable: false,
      headerAlign: 'left',
      align: 'left',
      width: 150,
      hideSortIcons: true,
    },
    {
      field: 'des_finalizadora',
      headerName: 'Finalizadora',
      editable: false,
      headerAlign: 'left',
      align: 'left',
      width: 225,
      hideSortIcons: true,
    },
  ];

  return (
    <Box sx={{ height: 300, width: '100%' }}>
      <DataGrid
        rows={agendamentos}
        columns={columns}
        disableColumnMenu
        loading={loading}
        disableSelectionOnClick
        hideFooterSelectedRowCount
        hideFooterRowCount
        rowsPerPageOptions={[5]}
        localeText={{
          noRowsLabel: 'Nenhum registro encontrado...',
          columnMenuLabel: 'Menu',
          columnMenuFilter: 'Filtrar',
          columnMenuHideColumn: 'Esconder',
          columnMenuUnsort: 'Não ordenar',
          columnMenuSortAsc: 'Ordernar ASC',
          columnMenuSortDesc: 'Ordernar DESC',
          columnMenuShowColumns: 'Mostrar colunas',
        }}
      />
    </Box>
  );
};
